// SPA entrypoint. Wires the customer-neutral DOM to the customer-specific
// CONFIG, handles the three-section flow (login -> home -> response), and
// runs the auth + privacy-API plumbing.
import { CONFIG } from "../generated/config.js";
import { login, logout, currentEmail, isLoggedIn } from "./auth.js";
import { submitPrivacyRequest } from "./api.js";

// ----- Section navigation -----
const SECTIONS = ["login-section", "home-section", "response-section"];
function show(target) {
  for (const id of SECTIONS) {
    document.getElementById(id).hidden = id !== target;
  }
  // Scroll to top on navigation — important on mobile after long forms.
  window.scrollTo({ top: 0, behavior: "instant" });
}

// ----- Apply per-customer branding to the DOM at startup -----
function applyBranding() {
  const b = CONFIG.branding;

  document.title = b.app_title;
  setText("brand-name", b.brand_name);
  setText("brand-subtitle", b.brand_subtitle);
  setText("login-label", b.login_label);
  setText("legal-footer", b.legal_footer);
  setAttr("brand-logo", "alt", `${b.brand_name} logo`);
  setAttr("username", "placeholder", b.email_placeholder);

  // Env banner — visible on every env except prod.
  const banner = document.getElementById("env-banner");
  if (CONFIG.env === "prod") {
    document.querySelector(".env-color-bar").style.display = "none";
    banner.hidden = true;
  } else {
    banner.textContent = CONFIG.envName;
    banner.hidden = false;
  }
}

function setText(id, text) {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}
function setAttr(id, attr, value) {
  const el = document.getElementById(id);
  if (el) el.setAttribute(attr, value);
}

// ----- Login -----
async function onLoginSubmit(e) {
  e.preventDefault();
  const username = document.getElementById("username").value.trim();
  const password = document.getElementById("password").value;
  const errorEl = document.getElementById("login-error");
  const button = document.getElementById("login-button");

  errorEl.hidden = true;
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  button.textContent = "Signing in";

  try {
    await login(username, password);
    document.getElementById("user-email").textContent =
      currentEmail() ?? username;
    document.getElementById("password").value = "";
    show("home-section");
  } catch (err) {
    errorEl.textContent = friendlyAuthError(err);
    errorEl.hidden = false;
  } finally {
    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.textContent = "Login";
  }
}

// ⚠️  All auth failures collapse to one message to prevent account
// enumeration. Per-error-code branching here let an attacker distinguish
// "user exists" (Incorrect username or password) from "user doesn't exist"
// (User does not exist), turning a random email list into a list of
// confirmed accounts. See docs/security-considerations.md.
//
// `newPasswordRequired` is the one case worth distinguishing because it's
// an admin-set state the user can't resolve by themselves and isn't useful
// to an enumeration attacker (admin-created accounts are not the typical
// enumeration target). Even there we keep the message generic.
function friendlyAuthError(err) {
  const msg = (err && err.message) || String(err);
  // Engineering visibility — full detail in DevTools console only.
  console.error("[auth] login failed:", err);

  if (/newPasswordRequired/i.test(msg) || /new password required/i.test(msg)) {
    return "Please contact support to complete your account setup.";
  }
  // Generic catch-all for: NotAuthorizedException, UserNotFoundException,
  // UserNotConfirmedException, PasswordResetRequiredException,
  // InvalidParameterException, TooManyRequestsException, LimitExceededException,
  // and any other Cognito error we haven't anticipated.
  return "Sign in failed. Please check your email and password, then try again.";
}

// ----- Home (the three action buttons) -----
const ACTION_BUTTONS = {
  sendData: "btn-send-data",
  deleteData: "btn-delete-data",
  deleteAccount: "btn-delete-account",
};

async function onAction(action) {
  if (!isLoggedIn()) {
    show("login-section");
    return;
  }

  const b = CONFIG.branding;

  // Confirmations run before we lock the UI — confirm() is blocking and
  // would visually freeze the spinner if we set aria-busy first.
  if (action === "deleteData") {
    if (
      !confirm(
        "Are you sure you want to request to delete your data? This action cannot be undone.",
      )
    )
      return;
  } else if (action === "deleteAccount") {
    if (!confirm(b.delete_account_first_confirm)) return;
    if (!confirm(b.delete_account_final_confirm)) return;
  }

  // Lock all three buttons while the API call is in flight; the active one
  // gets the spinner via aria-busy, the others stay disabled to prevent
  // rage-clicking another action mid-request.
  const allButtons = document.querySelectorAll(".btn-container button");
  const activeBtn = document.getElementById(ACTION_BUTTONS[action]);
  allButtons.forEach((btn) => {
    btn.disabled = true;
  });
  activeBtn.setAttribute("aria-busy", "true");

  let succeeded = false;
  try {
    await submitPrivacyRequest(action);
    succeeded = true;
  } catch (err) {
    // ⚠️  Surface only a generic message to the user. The raw err.message
    // can contain API Gateway / AVP / Lambda detail that leaks backend
    // internals. Engineering visibility lives in DevTools console only.
    console.error("[privacy-api] submission failed:", err);
    alert(
      "We couldn't submit your request. Please try again, or contact support if the problem persists.",
    );
  } finally {
    allButtons.forEach((btn) => {
      btn.disabled = false;
    });
    activeBtn.removeAttribute("aria-busy");
  }

  if (!succeeded) return;

  // Render the response section
  document.getElementById("response-message").textContent =
    humanResponse(action);
  const isAccountDelete = action === "deleteAccount";

  document.getElementById("auto-logout-box").hidden = !isAccountDelete;
  document.getElementById("back-link").hidden = isAccountDelete;
  document.getElementById("response-logout").hidden = isAccountDelete;

  show("response-section");

  if (isAccountDelete) {
    // Best-effort sign-out, then bounce back to login.
    try {
      await logout();
    } catch {}
    setTimeout(() => show("login-section"), 7500);
  }
}

function humanResponse(action) {
  switch (action) {
    case "sendData":
      return "Your data export request has been received. You will receive an email when your data is ready.";
    case "deleteData":
      return "Your data deletion request has been received and will be processed shortly.";
    case "deleteAccount":
      return "Your account deletion request has been received. Your account is being closed now.";
    default:
      return "Your request has been received.";
  }
}

// ----- Logout (manual) -----
async function onLogout(e) {
  if (e) e.preventDefault();
  try {
    await logout();
  } catch {}
  show("login-section");
}

// ----- Wire up -----
document.addEventListener("DOMContentLoaded", () => {
  applyBranding();

  document
    .getElementById("login-form")
    .addEventListener("submit", onLoginSubmit);

  document
    .getElementById("btn-send-data")
    .addEventListener("click", () => onAction("sendData"));
  document
    .getElementById("btn-delete-data")
    .addEventListener("click", () => onAction("deleteData"));
  document
    .getElementById("btn-delete-account")
    .addEventListener("click", () => onAction("deleteAccount"));

  document.getElementById("logout-link").addEventListener("click", onLogout);
  document
    .getElementById("response-logout")
    .addEventListener("click", onLogout);
  document.getElementById("back-link").addEventListener("click", (e) => {
    e.preventDefault();
    show("home-section");
  });
});
